'use client';

import { motion, useInView } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import {
  Database,
  Sparkles,
  PieChart,
  LayoutGrid,
  TrendingUp,
  MessageSquare,
  FileText,
  Zap,
  ShieldCheck,
  Layers,
  Activity,
} from 'lucide-react';

const EASE = [0.16, 1, 0.3, 1] as const;

// ─── Data ─────────────────────────────────────────────────────────────────────

const SOURCES = [
  { name: 'NetSuite',    type: 'ERP',         color: '#10B981' },
  { name: 'AppFolio',    type: 'Property',    color: '#F59E0B' },
  { name: 'Stripe',      type: 'Payments',    color: '#635BFF' },
  { name: 'Gusto',       type: 'Payroll',     color: '#F45D48' },
  { name: 'QuickBooks',  type: 'Accounting',  color: '#2CA01C' },
  { name: 'Salesforce',  type: 'CRM',         color: '#00A1E0' },
];

const STAGES = [
  { key: 'collect',  label: 'Collect',  sub: '∞ systems' },
  { key: 'enrich',   label: 'Enrich',   sub: '1,240 rows/min' },
  { key: 'allocate', label: 'Allocate', sub: '94% capture' },
];

const OUTPUTS = [
  { name: 'Live Dashboards',  meta: 'Real-time KPIs' },
  { name: 'AI Forecasts',     meta: 'Confidence bands' },
  { name: 'Board Reports',    meta: 'Auto-assembled' },
];

const CAPABILITIES = [
  { icon: PieChart,    title: 'AI Cost Allocation',    desc: 'Driver-based allocation across properties, products & people.' },
  { icon: TrendingUp,  title: 'Live Forecasting',      desc: 'Real-time forecasts that recalc as your business changes.' },
  { icon: Activity,    title: 'Variance Detection',    desc: 'Anomalies flagged the moment they appear, before they become problems.' },
  { icon: Layers,      title: 'Multi-entity Rollups',  desc: 'Roll up subsidiaries, currencies, and departments in one view.' },
];

// ═════════════════════════════════════════════════════════════════════════════
// MAIN
// ═════════════════════════════════════════════════════════════════════════════

export default function PlatformSection() {
  return (
    <section id="spine" className="relative py-20 md:py-28 overflow-hidden" style={{ backgroundColor: '#FBFAFF' }}>
      {/* Subtle grid texture only — top-anchored bloom removed so backgrounds line up between sections */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.035] pointer-events-none"
        style={{
          backgroundImage:
            'linear-gradient(rgba(76,29,149,1) 1px, transparent 1px), linear-gradient(90deg, rgba(76,29,149,1) 1px, transparent 1px)',
          backgroundSize: '72px 72px',
          maskImage: 'radial-gradient(ellipse at center, black 30%, transparent 80%)',
          WebkitMaskImage: 'radial-gradient(ellipse at center, black 30%, transparent 80%)',
        }}
      />

      <div className="relative container mx-auto max-w-7xl px-6">
        {/* ─── Section header ───────────────────────────────────────────── */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.7, ease: EASE }}
          className="text-center max-w-3xl mx-auto"
        >
          <div className="inline-flex items-center gap-3 text-[11px] tracking-[0.32em] uppercase text-purple-700/80 font-semibold">
            <span className="h-px w-8 bg-purple-300" />
            The Platform
            <span className="h-px w-8 bg-purple-300" />
          </div>
          <h2 className="mt-6 text-4xl md:text-6xl font-semibold tracking-[-0.035em] text-gray-900 leading-[1.02]">
            One system.{' '}
            <span className="bg-gradient-to-br from-[#6D28D9] via-[#7C3AED] to-[#A78BFA] bg-clip-text text-transparent">
              Every source. Every output.
            </span>
          </h2>
          <p className="mt-7 text-lg md:text-xl text-gray-600 leading-[1.6]">
            From raw data to executive insight: connected, enriched, allocated, and delivered{' '}
            <span className="font-semibold text-gray-900">in real time.</span>
          </p>
        </motion.div>

        {/* ─── Live stats bar ────────────────────────────────────────────── */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ delay: 0.15, duration: 0.6, ease: EASE }}
          className="mt-12 grid grid-cols-3 gap-3 md:gap-4 max-w-3xl mx-auto"
        >
          <LiveStat label="systems supported"><span className="font-normal">∞</span></LiveStat>
          <LiveStat label="rows / min" pulse><RowsPerMin /></LiveStat>
          <LiveStat label="source of truth"><span>1</span></LiveStat>
        </motion.div>

        {/* ─── DATA FLOW RIVER (the centerpiece) ─────────────────────────── */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ delay: 0.2, duration: 0.8, ease: EASE }}
          className="mt-16 md:mt-20"
        >
          <DataFlowRiver />
        </motion.div>

        {/* ─── Capability strip ──────────────────────────────────────────── */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ delay: 0.3, duration: 0.7, ease: EASE }}
          className="mt-16 md:mt-20"
        >
          <div className="text-center mb-8">
            <div className="text-[11px] tracking-[0.32em] uppercase text-purple-700/80 font-semibold">
              Built into every layer
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {CAPABILITIES.map((c, i) => (
              <CapabilityCard key={c.title} {...c} i={i} />
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// DATA FLOW RIVER (SVG visualization)
// ═════════════════════════════════════════════════════════════════════════════

function DataFlowRiver() {
  // ViewBox: 1200 × 600
  // Layout:
  //   Sources column:  x = 30  → 310   (6 cards)
  //   Processing zone: x = 380 → 870   (3 circular nodes)
  //   Outputs column:  x = 910 → 1190  (4 cards)

  const SRC_X = 30;
  const SRC_W = 280;
  const SRC_Y0 = 80;
  const SRC_GAP = 68;
  const SRC_H = 54;

  const NODE_R = 50;
  const NODE_Y = 300;
  const COLLECT_X = 460;
  const ENRICH_X  = 625;
  const ALLOC_X   = 790;

  const OUT_X = 910;
  const OUT_W = 280;
  const OUT_GAP = 96;
  const OUT_H = 64;
  // Keep the output stack vertically centered on the processing row, whatever the count
  const OUT_Y0 = NODE_Y - ((OUTPUTS.length - 1) * OUT_GAP + OUT_H) / 2;

  return (
    <div className="relative w-full overflow-hidden rounded-3xl border border-purple-100/70 bg-gradient-to-br from-purple-50/30 via-white to-purple-50/30 p-4 md:p-8 shadow-[0_40px_120px_-40px_rgba(76,29,149,0.35)]">
      {/* Top accent line */}
      <div aria-hidden className="absolute top-0 left-0 right-0 h-px" style={{ background: 'linear-gradient(90deg, transparent, #A78BFA, transparent)' }} />

      {/* MOBILE: vertical stacked flow (readable on small screens) */}
      <div className="md:hidden">
        <MobileDataFlow />
      </div>

      {/* DESKTOP / TABLET: the full horizontal SVG river */}
      <svg
        viewBox="0 0 1200 600"
        className="hidden md:block w-full h-auto"
        preserveAspectRatio="xMidYMid meet"
      >
        <defs>
          {/* Gradients */}
          <linearGradient id="flowGrad" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%"  stopColor="#A78BFA" stopOpacity="0.1" />
            <stop offset="50%" stopColor="#7C3AED" stopOpacity="0.7" />
            <stop offset="100%" stopColor="#6D28D9" stopOpacity="0.1" />
          </linearGradient>
          <linearGradient id="nodeGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#A78BFA" />
            <stop offset="100%" stopColor="#6D28D9" />
          </linearGradient>
          <linearGradient id="cardGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#FFFFFF" />
            <stop offset="100%" stopColor="#FAFAFA" />
          </linearGradient>
          <radialGradient id="nodeGlow" cx="0.5" cy="0.5" r="0.5">
            <stop offset="0%" stopColor="#A78BFA" stopOpacity="0.4" />
            <stop offset="100%" stopColor="#A78BFA" stopOpacity="0" />
          </radialGradient>
          {/* Glow filter for nodes */}
          <filter id="softGlow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="6" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* ═══ Column headers ═══ */}
        <ColumnHeader x={170} y={45} label="Sources" sub="all your systems" />
        <ColumnHeader x={625} y={45} label="AI Processing" sub="real-time" accent />
        <ColumnHeader x={1050} y={45} label="Outputs" sub="every channel" />

        {/* ═══ Flow paths — sources → Collect ═══ */}
        {SOURCES.map((_, i) => {
          const y = SRC_Y0 + i * SRC_GAP + SRC_H / 2;
          const id = `flow-in-${i}`;
          return (
            <path
              key={id}
              id={id}
              d={`M ${SRC_X + SRC_W} ${y} C ${SRC_X + SRC_W + 60} ${y}, ${COLLECT_X - 80} ${NODE_Y}, ${COLLECT_X - NODE_R} ${NODE_Y}`}
              fill="none"
              stroke="url(#flowGrad)"
              strokeWidth="1.5"
              opacity="0.45"
              strokeDasharray="3 3"
            />
          );
        })}

        {/* ═══ Internal flow — Collect → Enrich → Allocate ═══ */}
        <path
          id="flow-internal-1"
          d={`M ${COLLECT_X + NODE_R} ${NODE_Y} L ${ENRICH_X - NODE_R} ${NODE_Y}`}
          fill="none"
          stroke="#A78BFA"
          strokeWidth="2"
          opacity="0.65"
        />
        <path
          id="flow-internal-2"
          d={`M ${ENRICH_X + NODE_R} ${NODE_Y} L ${ALLOC_X - NODE_R} ${NODE_Y}`}
          fill="none"
          stroke="#A78BFA"
          strokeWidth="2"
          opacity="0.65"
        />

        {/* ═══ Flow paths — Allocate → outputs ═══ */}
        {OUTPUTS.map((_, i) => {
          const y = OUT_Y0 + i * OUT_GAP + OUT_H / 2;
          const id = `flow-out-${i}`;
          return (
            <path
              key={id}
              id={id}
              d={`M ${ALLOC_X + NODE_R} ${NODE_Y} C ${ALLOC_X + NODE_R + 60} ${NODE_Y}, ${OUT_X - 60} ${y}, ${OUT_X} ${y}`}
              fill="none"
              stroke="url(#flowGrad)"
              strokeWidth="1.5"
              opacity="0.45"
              strokeDasharray="3 3"
            />
          );
        })}

        {/* ═══ Source cards ═══ */}
        {SOURCES.map((s, i) => (
          <SourceCard
            key={s.name}
            x={SRC_X}
            y={SRC_Y0 + i * SRC_GAP}
            w={SRC_W}
            h={SRC_H}
            name={s.name}
            type={s.type}
            color={s.color}
            delay={i * 0.07}
          />
        ))}
        {/* "+ anything you use" */}
        <g transform={`translate(${SRC_X}, ${SRC_Y0 + 6 * SRC_GAP + 6})`}>
          <text x={SRC_W / 2} y={20} textAnchor="middle" className="text-[10px]" fill="#4B5563" style={{ fontSize: 12, fontWeight: 600, letterSpacing: '0.08em' }}>
            + anything else you use
          </text>
        </g>

        {/* ═══ Processing nodes ═══ */}
        <ProcessingNode cx={COLLECT_X} cy={NODE_Y} r={NODE_R} icon="db"      label={STAGES[0].label} sub={STAGES[0].sub} delay={0.5} surgeBegin={0} />
        <ProcessingNode cx={ENRICH_X}  cy={NODE_Y} r={NODE_R} icon="sparkle" label={STAGES[1].label} sub={STAGES[1].sub} delay={0.7} surgeBegin={0.5} />
        <ProcessingNode cx={ALLOC_X}   cy={NODE_Y} r={NODE_R} icon="pie"     label={STAGES[2].label} sub={STAGES[2].sub} delay={0.9} surgeBegin={1} />

        {/* ═══ Output cards ═══ */}
        {OUTPUTS.map((o, i) => (
          <OutputCard
            key={o.name}
            x={OUT_X}
            y={OUT_Y0 + i * OUT_GAP}
            w={OUT_W}
            h={OUT_H}
            name={o.name}
            meta={o.meta}
            delay={1.1 + i * 0.08}
          />
        ))}

        {/* ═══ PARTICLES (flowing) ═══ */}
        {/* Source → Collect */}
        {SOURCES.map((_, i) => (
          <Particles key={`pin-${i}`} pathId={`flow-in-${i}`} count={2} duration={3} delay={i * 0.4} color="#A78BFA" />
        ))}
        {/* Collect → Enrich */}
        <Particles pathId="flow-internal-1" count={3} duration={1.8} delay={0} color="#7C3AED" />
        {/* Enrich → Allocate */}
        <Particles pathId="flow-internal-2" count={3} duration={1.8} delay={0.6} color="#7C3AED" />
        {/* Allocate → outputs */}
        {OUTPUTS.map((_, i) => (
          <Particles key={`pout-${i}`} pathId={`flow-out-${i}`} count={2} duration={2.8} delay={i * 0.3} color="#6D28D9" />
        ))}

        {/* ═══ SUPERCHARGE PULSE — a bright glowing surge through the core pipeline ═══ */}
        <circle r="6" fill="#C4B5FD" filter="url(#softGlow)" opacity="0.95">
          <animateMotion dur="3s" repeatCount="indefinite" begin="0s"><mpath href="#flow-internal-1" /></animateMotion>
        </circle>
        <circle r="6" fill="#C4B5FD" filter="url(#softGlow)" opacity="0.95">
          <animateMotion dur="3s" repeatCount="indefinite" begin="1.5s"><mpath href="#flow-internal-2" /></animateMotion>
        </circle>
      </svg>
    </div>
  );
}

// ─── Mobile vertical flow (replaces SVG on small screens) ───────────────────

function MobileDataFlow() {
  return (
    <div className="flex flex-col items-stretch gap-4 py-2">
      {/* SOURCES */}
      <MobileColumnLabel label="Sources" sub="all your systems" />
      <div className="grid grid-cols-2 gap-2.5">
        {SOURCES.map((s, i) => (
          <motion.div
            key={s.name}
            initial={{ opacity: 0, y: 6 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, delay: i * 0.04, ease: EASE }}
            className="flex items-center gap-2.5 rounded-xl border border-purple-100/80 bg-white/90 px-3 py-2.5"
          >
            <span className="relative inline-flex h-5 w-5 items-center justify-center rounded-full" style={{ backgroundColor: `${s.color}22` }}>
              <span className="h-2 w-2 rounded-full" style={{ backgroundColor: s.color }} />
            </span>
            <div className="min-w-0">
              <div className="text-[12.5px] font-semibold text-gray-900 leading-tight truncate">{s.name}</div>
              <div className="text-[10.5px] text-gray-500 leading-tight truncate">{s.type}</div>
            </div>
          </motion.div>
        ))}
      </div>
      <div className="text-center text-[10.5px] font-medium tracking-[0.16em] text-gray-400 uppercase mt-0.5">+ anything else you use</div>

      {/* Connector */}
      <FlowConnector />

      {/* AI PROCESSING */}
      <MobileColumnLabel label="AI Processing" sub="real-time" accent />
      <div className="relative flex items-stretch justify-between gap-2.5">
        {STAGES.map((s, i) => (
          <motion.div
            key={s.key}
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.45, delay: i * 0.1, ease: EASE }}
            className="relative flex-1 rounded-2xl bg-gradient-to-br from-[#7C3AED] to-[#6D28D9] px-2 py-3.5 text-center shadow-[0_10px_30px_-12px_rgba(109,40,217,0.55)]"
          >
            <div className="mx-auto flex h-9 w-9 items-center justify-center rounded-full bg-white/15 backdrop-blur-sm ring-1 ring-white/25">
              {i === 0 && <Database className="h-4 w-4 text-white" strokeWidth={2} />}
              {i === 1 && <Sparkles className="h-4 w-4 text-white" strokeWidth={2} />}
              {i === 2 && <PieChart className="h-4 w-4 text-white" strokeWidth={2} />}
            </div>
            <div className="mt-2 text-[12.5px] font-semibold text-white leading-tight">{s.label}</div>
            <div className="mt-0.5 text-[10px] text-white/70 leading-tight">{s.sub}</div>
          </motion.div>
        ))}
        {/* connecting arrows between stages */}
        <div aria-hidden className="pointer-events-none absolute inset-0 flex items-center justify-around -z-0">
          <span className="absolute left-[33%] top-1/2 -translate-y-1/2 text-purple-300 text-xs">›</span>
          <span className="absolute left-[66%] top-1/2 -translate-y-1/2 text-purple-300 text-xs">›</span>
        </div>
      </div>

      {/* Connector */}
      <FlowConnector />

      {/* OUTPUTS */}
      <MobileColumnLabel label="Outputs" sub="every channel" />
      <div className="grid grid-cols-1 gap-2.5">
        {OUTPUTS.map((o, i) => (
          <motion.div
            key={o.name}
            initial={{ opacity: 0, y: 6 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, delay: i * 0.05, ease: EASE }}
            className="flex items-center gap-3 rounded-xl border border-purple-100/80 bg-white/90 px-3 py-2.5"
          >
            <span className="h-7 w-1 rounded-full bg-gradient-to-b from-[#A78BFA] to-[#6D28D9]" />
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full rounded-full bg-purple-500 opacity-75 animate-ping" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-purple-600" />
            </span>
            <div className="min-w-0 flex-1">
              <div className="text-[13px] font-semibold text-gray-900 leading-tight">{o.name}</div>
              <div className="text-[11px] text-gray-500 leading-tight">{o.meta}</div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

function MobileColumnLabel({ label, sub, accent }: { label: string; sub: string; accent?: boolean }) {
  return (
    <div className="flex items-center justify-center gap-2">
      <span className="h-px flex-1 bg-purple-200/60" />
      <div className="text-center">
        <div className={`text-[10.5px] font-bold tracking-[0.28em] uppercase ${accent ? 'text-purple-700' : 'text-gray-400'}`}>{label}</div>
        <div className="text-[9.5px] font-medium tracking-[0.12em] text-gray-400 mt-0.5">{sub}</div>
      </div>
      <span className="h-px flex-1 bg-purple-200/60" />
    </div>
  );
}

function FlowConnector() {
  return (
    <div className="flex justify-center py-1" aria-hidden>
      <div className="relative h-8 w-px bg-gradient-to-b from-purple-200 via-purple-400 to-purple-200 overflow-hidden">
        <motion.span
          className="absolute left-1/2 -translate-x-1/2 top-0 h-2 w-px bg-purple-600"
          animate={{ y: [0, 24, 0] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
        />
      </div>
    </div>
  );
}

// ─── Sub-components ──────────────────────────────────────────────────────────

function ColumnHeader({ x, y, label, sub, accent }: { x: number; y: number; label: string; sub: string; accent?: boolean }) {
  return (
    <g transform={`translate(${x}, ${y})`}>
      <text
        textAnchor="middle"
        style={{
          fontSize: 12,
          fontWeight: 700,
          letterSpacing: '0.32em',
          textTransform: 'uppercase',
        }}
        fill={accent ? '#6D28D9' : '#9CA3AF'}
      >
        {label}
      </text>
      <text
        y={16}
        textAnchor="middle"
        style={{ fontSize: 11, fontWeight: 500 }}
        fill="#6B7280"
      >
        {sub}
      </text>
    </g>
  );
}

function SourceCard({
  x, y, w, h, name, type, color, delay,
}: {
  x: number; y: number; w: number; h: number;
  name: string; type: string; color: string; delay: number;
}) {
  return (
    <motion.g
      initial={{ opacity: 0, x: -20 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, ease: EASE, delay }}
    >
      <rect x={x} y={y} width={w} height={h} rx={12} fill="url(#cardGrad)" stroke="#E9D5FF" strokeWidth={1} />
      {/* Color dot */}
      <circle cx={x + 24} cy={y + h / 2} r={10} fill={color} opacity={0.18} />
      <circle cx={x + 24} cy={y + h / 2} r={5} fill={color} />
      {/* Name + type */}
      <text x={x + 48} y={y + 24} style={{ fontSize: 14, fontWeight: 600 }} fill="#111827">
        {name}
      </text>
      <text x={x + 48} y={y + 42} style={{ fontSize: 11, fontWeight: 500 }} fill="#6B7280">
        {type}
      </text>
      {/* Live indicator */}
      <g transform={`translate(${x + w - 22}, ${y + h / 2})`}>
        <circle r={3} fill="#10B981">
          <animate attributeName="opacity" values="1;0.3;1" dur="2s" repeatCount="indefinite" />
        </circle>
      </g>
    </motion.g>
  );
}

function ProcessingNode({
  cx, cy, r, icon, label, sub, delay, surgeBegin = 0,
}: {
  cx: number; cy: number; r: number;
  icon: 'db' | 'sparkle' | 'pie';
  label: string; sub: string; delay: number; surgeBegin?: number;
}) {
  return (
    <motion.g
      initial={{ opacity: 0, scale: 0.6 }}
      whileInView={{ opacity: 1, scale: 1 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, ease: [0.34, 1.56, 0.64, 1], delay }}
    >
      {/* Glow */}
      <circle cx={cx} cy={cy} r={r + 14} fill="url(#nodeGlow)" />
      {/* Pulsing outer ring */}
      <circle cx={cx} cy={cy} r={r + 4} fill="none" stroke="#A78BFA" strokeWidth="1" opacity="0.4">
        <animate attributeName="r" values={`${r + 4};${r + 14};${r + 4}`} dur="3s" repeatCount="indefinite" />
        <animate attributeName="opacity" values="0.5;0;0.5" dur="3s" repeatCount="indefinite" />
      </circle>
      {/* Solid node */}
      <circle cx={cx} cy={cy} r={r} fill="url(#nodeGrad)" filter="url(#softGlow)" />
      {/* Inner stroke ring */}
      <circle cx={cx} cy={cy} r={r - 4} fill="none" stroke="white" strokeOpacity="0.25" strokeWidth="1.5" />
      {/* Sequential energy surge — fires Collect → Enrich → Allocate, reads as data being processed */}
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="#FFFFFF" strokeWidth="3" opacity="0">
        <animate attributeName="r" values={`${r - 2};${r + 12}`} dur="3s" begin={`${surgeBegin}s`} repeatCount="indefinite" />
        <animate attributeName="opacity" values="0;0.85;0" dur="3s" begin={`${surgeBegin}s`} repeatCount="indefinite" />
      </circle>

      {/* Icon (inline SVG paths) */}
      <IconInNode icon={icon} cx={cx} cy={cy} />

      {/* Label below */}
      <text x={cx} y={cy + r + 28} textAnchor="middle" style={{ fontSize: 15, fontWeight: 700, letterSpacing: '-0.01em' }} fill="#111827">
        {label}
      </text>
      <text x={cx} y={cy + r + 46} textAnchor="middle" style={{ fontSize: 11, fontWeight: 500 }} fill="#6B7280">
        {sub}
      </text>
    </motion.g>
  );
}

function IconInNode({ icon, cx, cy }: { icon: 'db' | 'sparkle' | 'pie'; cx: number; cy: number }) {
  // Hand-drawn simple icons in pure SVG for crisp rendering inside nodes
  const stroke = 'white';
  const sw = 2;
  if (icon === 'db') {
    return (
      <g transform={`translate(${cx - 12}, ${cy - 12})`}>
        <ellipse cx={12} cy={5}  rx={9} ry={3}  fill="none" stroke={stroke} strokeWidth={sw} />
        <path d="M 3 5 V 12 C 3 13.5 7 15 12 15 C 17 15 21 13.5 21 12 V 5" fill="none" stroke={stroke} strokeWidth={sw} />
        <path d="M 3 12 V 19 C 3 20.5 7 22 12 22 C 17 22 21 20.5 21 19 V 12" fill="none" stroke={stroke} strokeWidth={sw} />
      </g>
    );
  }
  if (icon === 'sparkle') {
    return (
      <g transform={`translate(${cx - 12}, ${cy - 12})`}>
        <path d="M 12 2 L 14 9 L 21 11 L 14 13 L 12 22 L 10 13 L 3 11 L 10 9 Z" fill="white" />
        <circle cx={20} cy={5} r={1.5} fill="white" />
      </g>
    );
  }
  // pie
  return (
    <g transform={`translate(${cx - 12}, ${cy - 12})`}>
      <circle cx={12} cy={12} r={10} fill="none" stroke={stroke} strokeWidth={sw} />
      <path d="M 12 2 A 10 10 0 0 1 22 12 L 12 12 Z" fill="white" />
    </g>
  );
}

function OutputCard({
  x, y, w, h, name, meta, delay,
}: {
  x: number; y: number; w: number; h: number;
  name: string; meta: string; delay: number;
}) {
  return (
    <motion.g
      initial={{ opacity: 0, x: 20 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, ease: EASE, delay }}
    >
      <rect x={x} y={y} width={w} height={h} rx={14} fill="url(#cardGrad)" stroke="#E9D5FF" strokeWidth={1} />
      {/* Left accent stripe */}
      <rect x={x} y={y} width={4} height={h} rx={2} fill="url(#nodeGrad)" />
      {/* Receiving particle indicator (animated arrival flash) */}
      <circle cx={x + 16} cy={y + h / 2} r={4} fill="#6D28D9" opacity={0.85}>
        <animate attributeName="opacity" values="0.5;1;0.5" dur="2.5s" repeatCount="indefinite" />
      </circle>
      {/* Text */}
      <text x={x + 32} y={y + 26} style={{ fontSize: 14, fontWeight: 600 }} fill="#111827">
        {name}
      </text>
      <text x={x + 32} y={y + 46} style={{ fontSize: 11, fontWeight: 500 }} fill="#6B7280">
        {meta}
      </text>
    </motion.g>
  );
}

// ─── Particle generator ──────────────────────────────────────────────────────

function Particles({
  pathId, count, duration, delay = 0, color,
}: { pathId: string; count: number; duration: number; delay?: number; color: string }) {
  return (
    <>
      {Array.from({ length: count }).map((_, i) => {
        const begin = delay + (i * duration) / count;
        return (
          <circle key={i} r="3.5" fill={color} opacity="0.95">
            <animateMotion dur={`${duration}s`} repeatCount="indefinite" begin={`${begin}s`}>
              <mpath href={`#${pathId}`} />
            </animateMotion>
            <animate
              attributeName="opacity"
              values="0;1;1;0"
              dur={`${duration}s`}
              repeatCount="indefinite"
              begin={`${begin}s`}
            />
          </circle>
        );
      })}
    </>
  );
}

// ─── Live stat tiles ─────────────────────────────────────────────────────────

function LiveStat({ children, label, pulse }: { children: React.ReactNode; label: string; pulse?: boolean }) {
  return (
    <div className="rounded-2xl border border-purple-100/80 bg-white/70 backdrop-blur px-4 py-3 text-center">
      <div className="text-2xl md:text-3xl font-semibold tracking-tight text-gray-900 tabular-nums leading-none">
        {children}
      </div>
      <div className="mt-2 flex items-center justify-center gap-1.5 text-[10px] tracking-[0.22em] uppercase text-gray-600 font-semibold">
        {pulse && (
          <span className="relative flex h-1.5 w-1.5">
            <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
            <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500" />
          </span>
        )}
        {label}
      </div>
    </div>
  );
}

// Counts up to 1,240 then ticks live — the pipeline is always moving
function RowsPerMin() {
  const ref = useRef<HTMLSpanElement>(null);
  const inView = useInView(ref, { once: true, margin: '-100px' });
  const [v, setV] = useState(0);
  useEffect(() => {
    if (!inView) return;
    let raf = 0;
    let iv: ReturnType<typeof setInterval> | undefined;
    const t0 = performance.now();
    const dur = 1400;
    const target = 1240;
    const live = () => {
      iv = setInterval(() => setV(1240 + Math.round((Math.random() - 0.5) * 120)), 2000);
    };
    const tick = (t: number) => {
      const p = Math.min(1, (t - t0) / dur);
      setV(Math.round(target * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(tick);
      else live();
    };
    raf = requestAnimationFrame(tick);
    return () => { cancelAnimationFrame(raf); if (iv) clearInterval(iv); };
  }, [inView]);
  return <span ref={ref}>{v.toLocaleString()}</span>;
}

// ─── Capability card ─────────────────────────────────────────────────────────

function CapabilityCard({
  icon: Icon, title, desc, i,
}: { icon: React.ElementType; title: string; desc: string; i: number }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-60px' }}
      transition={{ duration: 0.5, ease: EASE, delay: i * 0.08 }}
      className="group relative rounded-2xl border border-purple-100/70 bg-white p-5 hover:border-purple-200 hover:-translate-y-1 hover:shadow-[0_20px_60px_-25px_rgba(76,29,149,0.4)] transition-all overflow-hidden"
    >
      <div aria-hidden className="absolute -top-12 -right-12 h-32 w-32 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-3xl" style={{ background: 'radial-gradient(circle, rgba(124,58,237,0.3), transparent 70%)' }} />
      <div className="relative">
        <div className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-purple-50 to-purple-100/60 border border-purple-200/50 transition-transform group-hover:scale-110">
          <Icon className="h-5 w-5 text-purple-600" strokeWidth={2} />
        </div>
        <h4 className="mt-4 text-base font-semibold text-gray-900 tracking-tight">{title}</h4>
        <p className="mt-2 text-[14px] text-gray-600 leading-[1.6]">{desc}</p>
      </div>
    </motion.div>
  );
}
